// packages
import ky, { isHTTPError } from 'ky';
import type { BeforeRequestState, HTTPError, KyInstance } from 'ky';

/**
 * Returns the CSRF token from the meta tag. This is injected by Rails using
 * the `csrf_meta_tags` helper.
 *
 */
export function getCsrfToken(): string | undefined {
  return document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")
    ?.content;
}

/**
 * Audits whether a given URL is on the same origin as the current page. We do
 * not want to send the CSRF token to different origins because sometimes third-party
 * services will throw errors if they receive a header they don't expect.
 *
 * @private
 */
function isUrlSameOrigin(url: string): boolean {
  // it is a relative URL so same origin
  if (!url.startsWith('http')) {
    return true;
  }

  try {
    // if the hostname matches, it's the same origin
    return new URL(url).hostname === window.location.hostname;
  } catch (_) {
    // if something goes wrong with the URL parsing we err on the side of
    // caution and assume it's the same origin
    return true;
  }
}

/**
 */
function applyCsrfTokenToRequest({ request }: BeforeRequestState): void {
  // only need the CSRF token for non-GET requests
  if (request.method !== 'GET') {
    // send CSRF token only with same origin requests
    if (isUrlSameOrigin(request.url)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        request.headers.set('X-CSRF-Token', csrfToken);
      }
    }
  }
}

/**
 * Returns parsed error data from a ky HTTPError and rethrows unexpected errors.
 *
 */
export function getErrorData(error: unknown): HTTPError['data'] {
  if (isHTTPError(error)) {
    return error.data;
  }

  throw error;
}

const http: KyInstance = ky.create({
  hooks: { beforeRequest: [applyCsrfTokenToRequest] },
});

export default http;
